import { Combobox, Field } from '@payaca/ambrosia';
import { ListViewPage, SortDirection } from '@payaca/types/listViewTypes';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { getListedTemplatesPage } from '@payaca/store/templates/templatesActions';
import {
  PublicHydratedEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { ListedTemplatesPage, SortBy } from '@payaca/types/listedTemplateTypes';

interface Props<TTemplatableEntity extends TemplatableEntity> {
  label?: string;
  selectedTemplate?: PublicHydratedEntityTemplate<TTemplatableEntity>;
  onChange: (
    selectedTemplate?: PublicHydratedEntityTemplate<TTemplatableEntity>
  ) => void;
  placeholder?: string;
  allowedEntityTypes?: TTemplatableEntity[];
}

const TemplateSelectionControl = <T extends TemplatableEntity>({
  label,
  selectedTemplate,
  onChange,
  placeholder,
  allowedEntityTypes,
}: Props<T>): JSX.Element => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState<string>();
  const changeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [requiresGetListedTemplatesPage, setRequiresGetListedTemplatesPage] =
    useState(false);

  const [listedTemplatesPage, setListedTemplatesPage] = useState<
    ListViewPage<PublicHydratedEntityTemplate<TemplatableEntity>> | undefined
  >(undefined);

  const [isFetchingPage, setIsFetchingPage] = useState(false);

  useEffect(() => {
    if (changeTimeout?.current) {
      clearTimeout(changeTimeout.current);
    }
    changeTimeout.current = setTimeout(() => {
      setRequiresGetListedTemplatesPage(true);
    }, 500);
  }, [query]);

  const requestGetListedTemplatesPage = useCallback(() => {
    setIsFetchingPage(true);
    setRequiresGetListedTemplatesPage(false);

    dispatch(
      getListedTemplatesPage.request({
        queryParams: {
          sortDirection: SortDirection.DESCENDING,
          pageSize: 25,
          pageNumber: 1,
          searchTerm: query,
          sortBy: SortBy.NAME,
          entityTypes: allowedEntityTypes,
        },
        callback: (page: ListedTemplatesPage) => {
          setListedTemplatesPage(page);
          setIsFetchingPage(false);
        },
        onErrorCallback: () => {
          setIsFetchingPage(false);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [query, allowedEntityTypes]);

  useEffect(() => {
    requestGetListedTemplatesPage();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'requestGetListedTemplatesPage'
  }, []);

  useEffect(() => {
    if (requiresGetListedTemplatesPage) {
      requestGetListedTemplatesPage();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'requestGetListedTemplatesPage'
  }, [requiresGetListedTemplatesPage]);

  const options =
    listedTemplatesPage?.items.map((entityTemplate) => {
      let readableEntityType = 'Entity';

      switch (entityTemplate.entityType) {
        case 'scheduledEvent':
          readableEntityType = 'Event';
          break;
        case 'task':
          readableEntityType = 'Task';
          break;
        case 'proposal':
          readableEntityType = 'Proposal';
          break;
      }

      const entityName =
        'name' in entityTemplate.data ? entityTemplate.data.name : undefined;

      return {
        label:
          entityTemplate.name || entityName || `${readableEntityType} template`,
        value: entityTemplate.publicId,
        metadata: entityTemplate,
      };
    }) || [];

  return (
    <Field.Legacy name="template">
      <Field.Label>{label}</Field.Label>
      <Combobox
        options={options}
        value={selectedTemplate?.publicId}
        onChange={(value) => {
          const template = listedTemplatesPage?.items.find(
            (x) => x.publicId === value
          );
          onChange(template as PublicHydratedEntityTemplate<T>);
        }}
        placeholder={placeholder}
        query={query}
        setQuery={setQuery}
        filterFunction={() => true}
      />
    </Field.Legacy>
  );
};

export default TemplateSelectionControl;
